import { Skeleton, theme } from "antd"
import React, { Suspense, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { LazyPdfPages } from "../../../components/Esign/index.ts"
import { bytesOf } from "../../../components/Esign/signing/bytes.ts"
import { fetchOwnFields } from "../api/signing.services"
import type { OwnFields } from "../types/signing.types"
import PdfViewer from "./PdfViewer"

interface Props {
  session: string
  documentUrl: string
  documentBlob: Blob | null
  height: string
  /** Before consent the reader holds no fields yet, so the pages are drawn bare. */
  withFields?: boolean
}

type MarkedDocument = { bytes: Uint8Array; placement: OwnFields | null }

const SigningDocumentView: React.FC<Props> = ({
  session,
  documentUrl,
  documentBlob,
  height,
  withFields = true
}) => {
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const [marked, setMarked] = useState<MarkedDocument | null>(null)
  const [fallback, setFallback] = useState(!documentBlob)

  useEffect(() => {
    if (!documentBlob) {
      setFallback(true)
      return
    }
    let abandoned = false
    setMarked(null)
    setFallback(false)
    Promise.all([
      bytesOf(documentBlob),
      withFields ? fetchOwnFields(session) : null
    ])
      .then(([bytes, response]) => {
        if (abandoned) return
        if (!response) {
          setMarked({ bytes, placement: null })
          return
        }
        if (!response.ok || !response.data) {
          setFallback(true)
          return
        }
        setMarked({ bytes, placement: response.data })
      })
      .catch(() => {
        if (!abandoned) setFallback(true)
      })
    return () => {
      abandoned = true
    }
  }, [documentBlob, session, withFields])

  if (fallback) {
    return <PdfViewer url={documentUrl} height={height} />
  }

  if (!marked) {
    return (
      <div aria-busy aria-label={t("esign.pages.loading")}>
        <Skeleton.Node active className="!w-full" style={{ height }} />
      </div>
    )
  }

  return (
    <div
      className="overflow-auto rounded-lg p-2"
      style={{
        maxHeight: height,
        background: token.colorFillTertiary,
        border: `1px solid ${token.colorBorderSecondary}`
      }}
    >
      <Suspense fallback={<Skeleton active paragraph={{ rows: 8 }} />}>
        <LazyPdfPages
          data={marked.bytes}
          pages={marked.placement?.pages}
          onError={() => setFallback(true)}
        />
      </Suspense>
    </div>
  )
}

export default SigningDocumentView
